<template>
    <h5 class="link-header geist-font mx-4 mb-4" id="similar">Similar Patents</h5>
    <div class="patent-similarity d-flex w-full px-4 mb-4 flex-wrap">
        <div class="run-sim-button flex-grow-1">
            <div class="card-content h-full w-full">
                <div
                    v-if="!loadingSimilarity && patentList.length === 0 && !noPatents"
                    class="cta h-full d-flex align-items-center justify-content-center flex-wrap"
                >
                    <p class="geist-font large semi-bold w-full text-center">
                        How does this patent compare to its most similar counterparts?
                    </p>
                    <AonButton
                        class="mt-n8"
                        label="Run Similarity"
                        @clicked="onFindSimilarPatents"
                    />
                </div>
                <div
                    v-if="!loadingSimilarity && noPatents"
                    class="no-patents h-full d-flex align-items-center justify-content-center flex-wrap"
                >
                    <p class="geist-font large semi-bold w-full text-center">
                        No similar patents identified
                    </p>
                </div>
                <AonCoverLoading :loading="loadingSimilarity" title="Finding Similar Patents" />
            </div>
        </div>
        <AonCard v-if="patentList.length > 1" class="w-full mb-2">
            <div class="chart-holder">
                <p class="geist-font large semi-bold w-full">Timeline</p>
                <div id="similarPatentsChart"></div>
            </div>
            <div class="legend d-flex align-items-center">
                <div class="average d-flex align-items-center mr-6">
                    <div class="indicator mr-2"></div>
                    <p class="geist-font small">Avg. Filing Date</p>
                </div>
                <div class="similar d-flex align-items-center mr-6">
                    <div class="indicator mr-2"></div>
                    <p class="geist-font small">Similar Patents</p>
                </div>
                <div class="target d-flex align-items-center mr-4">
                    <div class="indicator mr-2"></div>
                    <p class="geist-font small">This Patent</p>
                </div>
            </div>
        </AonCard>
        <div v-if="patentList.length > 0" class="patent-list w-full">
            <div
                v-for="patent in patentList"
                :key="patent.id"
                class="patent-item pa-4 my-1"
                @click="goToGooglePatents(patent.googlePatentsLink)"
            >
                <p class="geist-font link patent-link">{{ patent.patentId }}</p>
                <p class="geist-font bold knights-cloak--text w-80 overflow-ellipsis">
                    {{ patent.title }}
                </p>
                <div class="info-container mt-4">
                    <div class="assignee d-flex my-2" ref="assigneeRef">
                        <div class="w-full d-flex align-items-center">
                            <font-awesome-icon
                                :icon="`fas fa-building`"
                                class="castle-stone-600--text mr-2 mt-n1"
                                v-tooltip="{
                                    content: 'Current Assignee',
                                    placement: 'left',
                                    delay: { show: 500, hide: 0 },
                                }"
                            />
                            <p
                                class="no-wrap overflow-ellipsis geist-font semi-bold castle-stone-600--text"
                            >
                                {{ patent?.entities ? patent?.entities : 'N/A' }}
                            </p>
                            <div class="divide"></div>
                            <p
                                class="no-wrap overflow-ellipsis geist-font semi-bold castle-stone-600--text"
                            >
                                <span>Ultimate Parent: </span
                                >{{ patent?.ultimateEntities ? patent?.ultimateEntities : 'N/A' }}
                            </p>
                        </div>
                    </div>
                    <div class="dates" ref="datesRef">
                        <div class="w-full d-flex align-items-center">
                            <font-awesome-icon
                                :icon="`fas fa-calendar`"
                                class="castle-stone-600--text mr-2 mt-n1"
                                v-tooltip="{
                                    content: 'Filing Date',
                                    placement: 'left',
                                    delay: { show: 500, hide: 0 },
                                }"
                            />
                            <p class="no-wrap overflow-ellipsis geist-font castle-stone-600--text">
                                <span>Filed </span>{{ filters.toUTCString(patent?.filingDate) }}
                            </p>
                            <div class="divide"></div>
                            <p class="no-wrap overflow-ellipsis geist-font castle-stone-600--text">
                                <span>Publication: </span
                                >{{ filters.toUTCString(patent?.publicationDate) }}
                            </p>
                            <div class="divide"></div>
                            <p class="no-wrap overflow-ellipsis geist-font castle-stone-600--text">
                                <span>Priority: </span
                                >{{ filters.toUTCString(patent?.priorityDate) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, inject, watch, nextTick, onMounted, onBeforeUnmount, defineExpose } from 'vue'
import { getSimilarPatents } from '@/api/patentViewer'
import { useEntityStore, usePatentViewerStore } from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useSimpleRangeBulletChart } from '@/composables/charts/simpleRangeBulletChart'
import { config } from '@/config'
import { debounce } from 'lodash-es'

const logger = inject('logger')
const filters = inject('filters')
const eventBus = inject('eventBus')

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()

const props = defineProps({
    patentDetails: {
        type: Object,
        default: () => ({}),
    },
    loadingPatentDetail: {
        type: Boolean,
        default: false,
    },
})

const loadingSimilarity = ref(false)
const patentList = ref([])
const noPatents = ref(false)
const chartData = ref(null)
const currentPatentId = ref(null)

let root
let simpleRangeBulletChart

// Debounced function for fetching similar patents
const debouncedFetchSimilarPatents = debounce(async (patentId, entityPk) => {
    await fetchSimilarPatents(patentId, entityPk)
}, 300)

onMounted(() => {
    // Listen for patent navigation events
    eventBus.on('patent-changed', handlePatentChanged)

    // Check if we already have data for the current patent
    if (props.patentDetails?.publicationPk) {
        checkCacheAndLoadData(props.patentDetails.publicationPk)
    }
})

onBeforeUnmount(() => {
    // Clean up event listeners
    eventBus.off('patent-changed', handlePatentChanged)

    // Dispose of chart resources
    if (root) {
        root.dispose()
        root = null
    }

    // Cancel any pending debounced calls
    debouncedFetchSimilarPatents.cancel()
})

// Handle patent navigation
const handlePatentChanged = (newPatentId) => {
    // Reset the component state
    resetComponentState()

    // Check cache for the new patent
    if (newPatentId) {
        checkCacheAndLoadData(newPatentId)
    }
}

// Reset component state
const resetComponentState = () => {
    patentList.value = []
    noPatents.value = false
    chartData.value = null

    // Clean up chart
    if (root) {
        root.dispose()
        root = null
    }
}

// Watch for patent detail changes
watch(
    () => props.patentDetails,
    (newVal, oldVal) => {
        if (newVal?.publicationPk && newVal.publicationPk !== currentPatentId.value) {
            // Patent has changed
            currentPatentId.value = newVal.publicationPk

            // Reset component state
            resetComponentState()

            // Check cache for the new patent
            checkCacheAndLoadData(newVal.publicationPk)
        }
    },
    { immediate: true }
)

const checkCacheAndLoadData = (patentId) => {
    if (!patentId) return

    // Check if we have cached data
    if (patentViewerStore.similarPatentsExistInCache(patentId)) {
        const cachedData = patentViewerStore.getCachedSimilarPatents(patentId)
        patentList.value = cachedData

        if (patentList.value.length === 0) {
            noPatents.value = true
        } else {
            // Use nextTick to ensure DOM is updated before initializing chart
            nextTick(() => {
                mapDataToChart()
            })
        }
    }
}

const onFindSimilarPatents = async () => {
    if (props.patentDetails?.publicationPk) {
        debouncedFetchSimilarPatents(
            props.patentDetails.publicationPk,
            entityStore.entity?.aon_entity_pk
        )
    }
}

const fetchSimilarPatents = async (patentId, entityPk) => {
    // If we already have cached data, use it
    if (patentViewerStore.similarPatentsExistInCache(patentId)) {
        const cachedData = patentViewerStore.getCachedSimilarPatents(patentId)
        patentList.value = cachedData

        if (patentList.value.length === 0) {
            noPatents.value = true
        } else {
            mapDataToChart()
        }
        return
    }

    loadingSimilarity.value = true
    try {
        const { data } = await getSimilarPatents(patentId, entityPk)
        patentList.value = data

        // Cache the results
        patentViewerStore.setCachedSimilarPatents(patentId, data)

        if (patentList.value.length === 0) {
            noPatents.value = true
            return
        }
    } catch (err) {
        logger.error(err)
        eventBus?.emit?.('snacktime', {
            type: 'error',
            message: 'Failed to fetch similar patents',
            contextual: true,
        })
        noPatents.value = true
    } finally {
        if (patentList.value.length > 1) {
            mapDataToChart()
        }
        loadingSimilarity.value = false
    }
}

const mapDataToChart = () => {
    if (patentList.value.length <= 0) return

    const thisPatentFilingDate = new Date(props.patentDetails?.filingDate).getTime()

    // Earliest filing date
    const earliestFilingDate = patentList.value.reduce((earliest, patent) => {
        const currentFilingDate = new Date(patent.filingDate).getTime()
        return currentFilingDate < earliest ? currentFilingDate : earliest
    }, thisPatentFilingDate) // Start with this patent's filing date as the initial value

    // Latest filing date
    const latestFilingDate = patentList.value.reduce((latest, patent) => {
        const currentFilingDate = new Date(patent.filingDate).getTime()
        return currentFilingDate > latest ? currentFilingDate : latest
    }, thisPatentFilingDate) // Start with this patent's filing date as the initial value

    // Average
    const averageFilingDate =
        (patentList.value.reduce((sum, patent) => {
            return sum + new Date(patent.filingDate).getTime()
        }, 0) +
            thisPatentFilingDate) /
        (patentList.value.length + 1)

    // All patent dates (excluding earliest, latest, and this patent's filing date)
    const allPatentDates = patentList.value
        .filter((patent) => {
            const filingDate = new Date(patent.filingDate).getTime()
            return (
                filingDate !== earliestFilingDate &&
                filingDate !== latestFilingDate &&
                patent.patentId !== props.patentDetails?.patentId
            )
        })
        .map((patent) => {
            return {
                date: new Date(patent.filingDate).getTime(),
                id: patent.patentId,
            }
        })

    chartData.value = [
        {
            name: 'Similar Patents Timeline',
            entry: earliestFilingDate,
            latest: latestFilingDate,
            target: thisPatentFilingDate,
            average: averageFilingDate,
            patentDates: allPatentDates,
        },
    ]

    // Use nextTick to ensure the DOM is updated before initializing the chart
    nextTick(() => {
        initializeChart()
    })
}

const initializeChart = async () => {
    // Clean up existing chart if it exists
    if (root) {
        root.dispose()
        root = null
    }

    await nextTick()

    // Only initialize chart if the component is still mounted and we have data
    if (!chartData.value || chartData.value.length === 0) return

    try {
        am5.addLicense(config.license.AMChartsLicense)
        root = am5.Root.new('similarPatentsChart')

        simpleRangeBulletChart = useSimpleRangeBulletChart(root)

        simpleRangeBulletChart.configureSimpleRangeBullet()
        simpleRangeBulletChart.generateAxes(chartData.value)
        simpleRangeBulletChart.generateSeries(chartData.value)
    } catch (err) {
        logger.error('Error initializing chart:', err)
    }
}

const goToGooglePatents = (googleLink) => {
    if (!googleLink) return
    window.open(`${googleLink}`, '_blank')
}

defineExpose({
    onFindSimilarPatents,
    patentList,
    loadingSimilarity,
    noPatents,
})
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.patent-similarity {
    min-height: 200px;

    position: relative;

    .patent-item {
        background: rgba(vars.$knightsCloak20, 0.1);
        border: solid 2px transparent;
        cursor: pointer;

        position: relative;

        transition: all 0.2s ease-in-out;

        &:hover {
            background: rgba(vars.$castleMoat, 0.1);
            border-color: vars.$castleMoat;
        }

        &:first-of-type {
            border-radius: 8px 8px 0 0;
        }

        &:last-of-type {
            border-radius: 0 0 8px 8px;
        }
    }

    .patent-link {
        position: absolute;
        top: 20px;
        right: 20px;
    }

    .divide {
        border-radius: 50%;
        width: 5px;
        height: 5px;
        background-color: vars.$grey04;
        margin: 0 12px;
    }

    #similarPatentsChart {
        height: 100%;
        height: 130px;

        div {
            height: 100%;
        }
    }

    .legend {
        position: absolute;
        top: 20px;
        right: 20px;

        .average {
            .indicator {
                background-color: vars.$castleMoat;
                width: 4px;
                height: 20px;
            }
        }

        .similar,
        .target {
            .indicator {
                background-color: vars.$dungeonDepths;
                width: 7px;
                height: 7px;
                border-radius: 50%;
            }
        }

        .target {
            .indicator {
                background-color: vars.$gold;
                width: 14px;
                height: 14px;
            }
        }
    }
}
</style>
